//Register.js
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { register } from "../actions/authenActions";
import { Box, Button, TextField, Typography, Alert } from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import Layout from "./Layout";

const Register = () => {
  const [formData, setFormData] = useState({
    user_id: "",
    password: "",
    confirmPassword: "",
    license_key: "",
    machine_uuid: "00000000-0000-0000-0000-000000000000",
    deployment_type: "web",
  });

  const [formErrors, setFormErrors] = useState({});
  const [submissionError, setSubmissionError] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const registeredUser = useSelector((state) => state.authen.registeredUser);

  useEffect(() => {
    if (registeredUser) {
      if (registeredUser.status === "Pass") {
        navigate("/login");
      } else {
        setSubmissionError(registeredUser.status || "Registration failed");
      }
    }
  }, [registeredUser, navigate]);

  const validate = () => {
    const errors = {};

    // Check all fields filled
    if (!formData.user_id) {
      errors.user_id = "Username is required";
    } else {
      if (formData.user_id.length < 4) {
        errors.user_id = "Username must be at least 4 characters";
      } else if (!/^[a-zA-Z0-9]+$/.test(formData.user_id)) {
        errors.user_id = "Username must be alphanumeric";
      }
    }

    if (!formData.password) {
      errors.password = "Password is required";
    } else {
      if (formData.password.length < 12) {
        errors.password = "Password must be at least 12 characters";
      } else if (formData.password.length > 24) {
        errors.password = "Password must be less than 24 characters";
      } else if (!/(?=.*[a-z])/.test(formData.password)) {
        errors.password = "Password must contain at least one lowercase letter";
      } else if (!/(?=.*[A-Z])/.test(formData.password)) {
        errors.password = "Password must contain at least one uppercase letter";
      } else if (!/(?=.*\d)/.test(formData.password)) {
        errors.password = "Password must contain at least one digit";
      } else if (
        !/(?=.*[[\]!@#$%^&*()_\-+=\]{};':"\\|,.<>/?])/.test(formData.password)
      ) {
        errors.password =
          "Password must contain at least one special character";
      }
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (!formData.license_key) {
      errors.license_key = "License text is required";
    }

    return errors;
  };

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
    setSubmissionError(null);
  }

  function handleSubmit(e) {
    e.preventDefault();

    const errors = validate();
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    dispatch(register(formData))
      .then(() => {
        // handled in useEffect
      })
      .catch((error) => {
        setSubmissionError(error?.message || "An unexpected error occurred");
      });
  }

  return (
    <Layout>
      <Typography variant="h5">Create a username and password</Typography>

      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        {submissionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submissionError}
          </Alert>
        )}

        <TextField
          name="user_id"
          label="Username"
          fullWidth
          margin="normal"
          value={formData.user_id}
          onChange={handleChange}
          error={!!formErrors.user_id}
          helperText={formErrors.user_id}
        />

        <TextField
          name="password"
          label="Password"
          type="password"
          fullWidth
          margin="normal"
          value={formData.password}
          onChange={handleChange}
          error={!!formErrors.password}
          helperText={formErrors.password}
        />

        <TextField
          name="confirmPassword"
          label="Reconfirm Password"
          type="password"
          fullWidth
          margin="normal"
          value={formData.confirmPassword}
          onChange={handleChange}
          error={!!formErrors.confirmPassword}
          helperText={formErrors.confirmPassword}
        />

        <TextField
          name="license_key"
          label="License Text"
          multiline
          rows={4}
          fullWidth
          margin="normal"
          value={formData.license_key}
          onChange={handleChange}
          error={!!formErrors.license_key}
          helperText={formErrors.license_key}
        />

        <Button fullWidth variant="contained" type="submit" sx={{ mt: 2 }}>
          Sign Up
        </Button>

        <Box mt={2}>
          <Link to="/login">Already have an account? Log In</Link>
        </Box>
      </Box>
    </Layout>
  );
};

export default Register;
